(function () {
  const SECTION = '.le7-mlp[data-section-type="le7-mlp"]'
  const TILE_ORDER = ['hero', 'top-right', 'bottom-left', 'bottom-right']
  const PHASE_COUNT = 5
  const PHASE_SEGMENTS = PHASE_COUNT - 1
  const FOCUS_CELL_INDEX = [null, 0, 1, 2, 3]
  /**
   * Every tile keeps ONE aspect ratio in every phase (matches the comp PNGs,
   * 607 x 1024). Only the size changes, never the proportion. The large tile
   * sits in the featured corner; the three small tiles form an L-cluster in the
   * opposite corner, exactly like the approved mockups.
   */
  const TILE_R = 1024 / 607 // tile height / width (all tiles share this)
  const SMALL = 0.42 // small tile width, with large tile width = 1
  const GAP_U = 0.06 // gap as a fraction of the large tile width

  function lerp(a, b, t) {
    return a + (b - a) * t
  }

  function easeInOutSine(t) {
    const clamped = Math.min(1, Math.max(0, t))
    return -(Math.cos(Math.PI * clamped) - 1) / 2
  }

  /**
   * Bounding-box model in "large-width units" (large tile width = 1).
   * The whole mosaic is exactly Wb × Hb units, so equal real gaps render as
   * equal pixels and every tile keeps the TILE_R aspect ratio.
   */
  function boxModel() {
    const L = TILE_R // large tile height
    const sw = SMALL // small tile width
    const sh = SMALL * TILE_R // small tile height (same aspect)
    const g = GAP_U
    const Wb = 1 + g + sw // bounding-box width
    const Hb = L + g + sh // bounding-box height
    const ew = (Wb - g) / 2 // equal-grid tile width
    const eh = ew * TILE_R // equal-grid tile height (same aspect)
    return { L, sw, sh, g, Wb, Hb, ew, eh }
  }

  function tile(x, y, w, h) {
    return { x, y, w, h }
  }

  /** Phase 0: balanced 2×2 grid, every tile the shared aspect ratio. */
  function phaseEqual(B) {
    const { ew, eh, Wb, Hb } = B
    return {
      hero: tile(0, 0, ew, eh),
      'top-right': tile(Wb - ew, 0, ew, eh),
      'bottom-left': tile(0, Hb - eh, ew, eh),
      'bottom-right': tile(Wb - ew, Hb - eh, ew, eh),
    }
  }

  /**
   * Featured comps. The named corner holds the large tile; the other three
   * keep their fixed identity and cluster into an L in the opposite corner.
   * Tiles never swap corners and never change aspect ratio.
   */
  function phaseFeatured(B, corner) {
    const { L, sw, sh, Wb, Hb } = B
    switch (corner) {
      case 'hero': // large top-left
        return {
          hero: tile(0, 0, 1, L),
          'top-right': tile(Wb - sw, L - sh, sw, sh),
          'bottom-left': tile(1 - sw, Hb - sh, sw, sh),
          'bottom-right': tile(Wb - sw, Hb - sh, sw, sh),
        }
      case 'top-right': // large top-right
        return {
          hero: tile(0, L - sh, sw, sh),
          'top-right': tile(Wb - 1, 0, 1, L),
          'bottom-left': tile(0, Hb - sh, sw, sh),
          'bottom-right': tile(Wb - 1, Hb - sh, sw, sh),
        }
      case 'bottom-left': // large bottom-left
        return {
          hero: tile(1 - sw, 0, sw, sh),
          'top-right': tile(Wb - sw, 0, sw, sh),
          'bottom-left': tile(0, Hb - L, 1, L),
          'bottom-right': tile(Wb - sw, Hb - L, sw, sh),
        }
      case 'bottom-right': // large bottom-right
        return {
          hero: tile(0, 0, sw, sh),
          'top-right': tile(Wb - 1, 0, sw, sh),
          'bottom-left': tile(0, Hb - L, sw, sh),
          'bottom-right': tile(Wb - 1, Hb - L, 1, L),
        }
      default:
        return phaseEqual(B)
    }
  }

  function buildPhases(mw, mh) {
    const B = boxModel()
    const sx = mw / B.Wb
    const sy = mh / B.Hb
    const scale = (phase) => {
      const out = {}
      TILE_ORDER.forEach((key) => {
        const r = phase[key]
        out[key] = { x: r.x * sx, y: r.y * sy, w: r.w * sx, h: r.h * sy }
      })
      return out
    }
    return [
      scale(phaseEqual(B)),
      scale(phaseFeatured(B, 'hero')),
      scale(phaseFeatured(B, 'top-right')),
      scale(phaseFeatured(B, 'bottom-left')),
      scale(phaseFeatured(B, 'bottom-right')),
    ]
  }

  function mixTilePhase(p0, p1, t) {
    const tiles = {}
    TILE_ORDER.forEach((key) => {
      const a = p0[key]
      const b = p1[key]
      tiles[key] = {
        x: lerp(a.x, b.x, t),
        y: lerp(a.y, b.y, t),
        w: lerp(a.w, b.w, t),
        h: lerp(a.h, b.h, t),
      }
    })
    return tiles
  }

  function mapScrollProgress(progress) {
    const clamped = Math.min(1, Math.max(0, progress))
    const segment = 1 / PHASE_SEGMENTS
    const index = Math.min(PHASE_SEGMENTS - 1, Math.floor(clamped / segment))
    const local = (clamped - index * segment) / segment
    const localT = easeInOutSine(local)
    const activePhase = index + (localT >= 0.5 ? 1 : 0)
    return { clamped, index, localT, activePhase }
  }

  function layoutAtProgress(progress, mosaic) {
    const mw = mosaic.offsetWidth
    const mh = mosaic.offsetHeight
    if (!mw || !mh) return null

    const phases = buildPhases(mw, mh)
    const { index, localT } = mapScrollProgress(progress)
    return mixTilePhase(phases[index], phases[index + 1], localT)
  }

  function clampTileBox(left, top, width, height, mosaicWidth, mosaicHeight) {
    const safeLeft = Math.min(Math.max(0, left), Math.max(0, mosaicWidth - 1))
    const safeTop = Math.min(Math.max(0, top), Math.max(0, mosaicHeight - 1))
    const safeWidth = Math.min(Math.max(0, width), mosaicWidth - safeLeft)
    const safeHeight = Math.min(Math.max(0, height), mosaicHeight - safeTop)
    return { left: safeLeft, top: safeTop, width: safeWidth, height: safeHeight }
  }

  function applyTileLayout(mosaic, tiles) {
    const mosaicWidth = mosaic.offsetWidth
    const mosaicHeight = mosaic.offsetHeight
    if (!mosaicWidth || !mosaicHeight) return

    TILE_ORDER.forEach((key) => {
      const tileData = tiles[key]
      const cell = mosaic.querySelector(`[data-tile="${key}"]`)
      if (!cell || !tileData) return

      const box = clampTileBox(
        tileData.x,
        tileData.y,
        tileData.w,
        tileData.h,
        mosaicWidth,
        mosaicHeight
      )

      const w = Math.round(box.width * 100) / 100
      const h = Math.round(box.height * 100) / 100
      const x = Math.round(box.left * 100) / 100
      const y = Math.round(box.top * 100) / 100
      cell.style.width = `${w}px`
      cell.style.height = `${h}px`
      cell.style.transform = `translate3d(${x}px, ${y}px, 0)`
    })
  }

  function getCells(root) {
    return TILE_ORDER.map((tile) => root.querySelector(`[data-tile="${tile}"]`)).filter(Boolean)
  }

  function applyDeck(cells, progress) {
    const { activePhase, localT } = mapScrollProgress(progress)
    const focusIndex = FOCUS_CELL_INDEX[activePhase]
    const focusBlend =
      focusIndex === null ? 0 : Math.min(1, Math.max(0, (localT - 0.28) / 0.72))

    cells.forEach((cell, index) => {
      const stackBase = parseInt(cell.dataset.stack || String(index + 1), 10)
      const isFocus = focusIndex !== null && index === focusIndex
      const z = isFocus ? 8 + Math.round(focusBlend * 6) : stackBase
      cell.style.setProperty('--le7-cell-z', String(z))
    })
  }

  function applyCopy(root, tiles) {
    const items = root.querySelectorAll('[data-le7-copy-item]')
    if (!items.length || !tiles) return

    // Show the copy for whichever tile is currently the LARGEST. During a
    // transition the outgoing (shrinking) tile stays largest until the incoming
    // (growing) tile overtakes it, so the copy only flips once the next tile
    // actually gets bigger than the current one — not the instant it starts
    // growing. Ties keep the earlier tile in TILE_ORDER (the outgoing one),
    // so the switch happens exactly at the size crossover.
    let activeCopy = 0
    let maxArea = -Infinity
    TILE_ORDER.forEach((key, i) => {
      const t = tiles[key]
      if (!t) return
      const area = t.w * t.h
      if (area > maxArea) {
        maxArea = area
        activeCopy = i
      }
    })

    items.forEach((item) => {
      const i = parseInt(item.dataset.copyIndex || '0', 10)
      item.classList.toggle('is-active', i === activeCopy)
    })
  }

  function applyFrame(root, mosaic, cells, progress) {
    const layout = layoutAtProgress(progress, mosaic, root)
    if (!layout) return
    applyTileLayout(mosaic, layout)
    applyDeck(cells, progress)
    applyCopy(root, layout)
    root.style.setProperty('--le7-copy-opacity', '1')
    root.dataset.le7Progress = progress.toFixed(3)
  }

  function setLayoutMode(root, mosaic, animated) {
    root.classList.toggle('le7-mlp--scroll-layout', animated)
    root.classList.remove('le7-mlp--grid-scale', 'le7-mlp--parallax')
    mosaic.classList.toggle('le7-mlp__mosaic--absolute', animated)
  }

  function clearTileLayout(cells) {
    cells.forEach((cell) => {
      cell.style.transform = ''
      cell.style.width = ''
      cell.style.height = ''
      cell.style.left = ''
      cell.style.top = ''
      cell.style.removeProperty('--le7-tile-scale')
    })
  }

  function getRunwayPx(root) {
    const isDesktop = window.matchMedia('(min-width: 750px)').matches
    const setting = isDesktop
      ? parseFloat(root.dataset.scrollRunwayDesktop || '420')
      : parseFloat(root.dataset.scrollRunwayMobile || '380')
    return Math.round(
      (window.visualViewport?.height || window.innerHeight) * (setting / 100)
    )
  }

  function getScrub() {
    return window.matchMedia('(min-width: 750px)').matches ? 0.6 : 0.5
  }

  function getPinStartOffset() {
    const header =
      document.querySelector('.shopify-section-header-sticky') ||
      document.querySelector('.section-header-mobile.shopify-section-header-sticky') ||
      document.querySelector('.section-header-mobile') ||
      document.querySelector('header')
    if (!header) return 0
    return Math.max(0, Math.round(header.getBoundingClientRect().height))
  }

  function applyHeaderOffset(root) {
    root.style.setProperty('--le7-header-offset', `${getPinStartOffset()}px`)
  }

  function getZoneScrollProgress(zone) {
    const viewport = window.visualViewport?.height || window.innerHeight
    const total = zone.offsetHeight - viewport
    if (total <= 0) return 0

    const rectTop = zone.getBoundingClientRect().top
    const fromRect = Math.min(Math.max(-rectTop, 0), total)
    const start = zone.offsetTop
    const scrollY = window.scrollY || document.documentElement.scrollTop || 0
    const fromOffset = Math.min(Math.max(scrollY - start, 0), total)
    return Math.max(fromRect, fromOffset) / total
  }

  function destroySection(root) {
    root._le7ScrollTrigger?.kill()
    root._le7ScrollTrigger = null

    if (root._le7ScrollHandler) {
      window.removeEventListener('scroll', root._le7ScrollHandler, true)
      document.removeEventListener('scroll', root._le7ScrollHandler, true)
      root._le7ScrollHandler = null
    }
    if (root._le7ResizeHandler) {
      window.removeEventListener('resize', root._le7ResizeHandler)
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', root._le7ResizeHandler)
        window.visualViewport.removeEventListener('scroll', root._le7ScrollHandler)
      }
      root._le7ResizeHandler = null
    }

    const mosaic = root.querySelector('[data-le7-mosaic]')
    clearTileLayout(getCells(root))
    root.dataset.le7Init = 'false'
    root.dataset.le7Progress = ''
    root.removeAttribute('data-le7-engine')
  }

  function initNativeScroll(root, zone, mosaic, cells) {
    function update() {
      applyHeaderOffset(root)
      applyFrame(root, mosaic, cells, getZoneScrollProgress(zone))
    }

    let ticking = false
    function onScroll() {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        ticking = false
        update()
      })
    }

    root._le7ScrollHandler = onScroll
    root._le7ResizeHandler = update
    window.addEventListener('scroll', onScroll, { passive: true, capture: true })
    document.addEventListener('scroll', onScroll, { passive: true, capture: true })
    window.addEventListener('resize', update, { passive: true })
    if (window.visualViewport) {
      window.visualViewport.addEventListener('scroll', onScroll, { passive: true })
      window.visualViewport.addEventListener('resize', update, { passive: true })
    }

    update()
    requestAnimationFrame(() => {
      update()
      requestAnimationFrame(update)
    })
    root.dataset.le7Engine = 'comp-layout-native'
  }

  function initScrollTrigger(root, zone, pin, mosaic, cells) {
    const pinOffset = getPinStartOffset()
    const isTouch =
      ScrollTrigger.isTouch > 0 ||
      window.matchMedia('(hover: none) and (pointer: coarse)').matches

    root._le7ScrollTrigger = ScrollTrigger.create({
      trigger: zone,
      start: () => `top top+=${pinOffset}`,
      end: () => `+=${getRunwayPx(root)}`,
      pin: pin,
      pinSpacing: true,
      pinType: 'fixed',
      scrub: getScrub(),
      smoothTouch: isTouch ? 0.08 : false,
      invalidateOnRefresh: true,
      anticipatePin: 1,
      onUpdate: (self) => {
        applyFrame(root, mosaic, cells, self.progress)
      },
      onRefresh: (self) => {
        applyFrame(root, mosaic, cells, self.progress)
      },
    })

    requestAnimationFrame(() => {
      ScrollTrigger.refresh()
      if (root._le7ScrollTrigger) {
        applyFrame(root, mosaic, cells, root._le7ScrollTrigger.progress)
      }
    })
    root.dataset.le7Engine = 'comp-layout-gsap'
  }

  function gsapReady() {
    return typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined'
  }

  function initSection(root, options = {}) {
    const force = options.force === true
    if (root.dataset.le7Init === 'true' && !force) return true

    const zone = root.querySelector('.le7-mlp__scroll-zone')
    const pin = root.querySelector('.le7-mlp__pin')
    const mosaic = root.querySelector('[data-le7-mosaic]')
    const cells = getCells(root)

    if (!zone || !pin || !mosaic || cells.length !== 4) return false

    destroySection(root)
    applyHeaderOffset(root)

    const animated =
      root.dataset.animation === 'true' &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches

    setLayoutMode(root, mosaic, animated)

    if (!animated) {
      applyFrame(root, mosaic, cells, 0)
      root.dataset.le7Init = 'true'
      root.dataset.le7Engine = 'static'
      return true
    }

    applyFrame(root, mosaic, cells, 0)

    if (gsapReady()) {
      gsap.registerPlugin(ScrollTrigger)
      // Ignore viewport height changes from the mobile address bar so the pin
      // doesn't jump/recalculate mid-scroll.
      ScrollTrigger.config({ ignoreMobileResize: true })
      initScrollTrigger(root, zone, pin, mosaic, cells)
    } else {
      initNativeScroll(root, zone, mosaic, cells)
    }

    root.dataset.le7Init = 'true'
    return true
  }

  function initAll(options = {}) {
    let ok = true
    document.querySelectorAll(SECTION).forEach((root) => {
      if (!initSection(root, options)) ok = false
    })
    if (gsapReady()) ScrollTrigger.refresh()
    return ok
  }

  function boot(options = {}) {
    initAll(options)

    document.querySelectorAll(`${SECTION} img`).forEach((img) => {
      if (img.complete) return
      img.addEventListener(
        'load',
        () => {
          if (gsapReady()) ScrollTrigger.refresh()
          else initAll({ force: true, ...options })
        },
        { once: true }
      )
    })

    if (!gsapReady()) {
      let attempts = 0
      const retry = setInterval(() => {
        attempts += 1
        if (gsapReady()) {
          clearInterval(retry)
          initAll({ force: true, ...options })
          ScrollTrigger.refresh(true)
        } else if (attempts >= 80) {
          clearInterval(retry)
        }
      }, 50)
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot())
  } else {
    boot()
  }

  window.addEventListener('load', () => boot({ force: true }))

  // Only fully re-init on a real width change (orientation / window resize).
  // Mobile browsers fire resize on every address-bar show/hide (height only);
  // re-booting then would reset the animation and feel broken, so we skip it.
  let lastWidth = window.innerWidth
  let resizeTimer = 0
  window.addEventListener('resize', () => {
    if (window.innerWidth === lastWidth) return
    lastWidth = window.innerWidth
    clearTimeout(resizeTimer)
    resizeTimer = setTimeout(() => boot({ force: true }), 200)
  })

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector?.(SECTION) || event.target.closest?.(SECTION)
    if (root) initSection(root, { force: true })
  })

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector?.(SECTION)
    if (root) destroySection(root)
  })
})()
